import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Typography,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Pagination,
  CircularProgress,
  Alert,
  Chip,
  Divider,
  Paper,
  InputAdornment,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CloseIcon from '@mui/icons-material/Close';
import { apiClient } from '../services/apiClient';

// Helper to strip HTML tags for plain text preview
const stripHtml = (html) => {
  if (!html) return '';
  const tmp = document.createElement('DIV');
  tmp.innerHTML = html;
  return tmp.textContent || tmp.innerText || '';
};

// Helper to truncate HTML while preserving structure
const truncateHtml = (html, maxLength = 150) => {
  if (!html) return 'N/A';
  
  // Check if it's actually HTML (contains tags)
  const hasHtmlTags = /<[^>]+>/.test(html);
  
  if (!hasHtmlTags) {
    // Plain text, just truncate normally
    if (html.length <= maxLength) return html;
    return html.substring(0, maxLength) + '...';
  }
  
  // Parse HTML and truncate intelligently
  const tmp = document.createElement('DIV');
  tmp.innerHTML = html;
  
  let textLength = 0;
  let truncated = false;
  
  const truncateNode = (node) => {
    if (truncated) return;
    
    if (node.nodeType === Node.TEXT_NODE) {
      const text = node.textContent || '';
      if (textLength + text.length <= maxLength) {
        textLength += text.length;
      } else {
        // Truncate this text node
        const remaining = maxLength - textLength;
        node.textContent = text.substring(0, remaining) + '...';
        textLength = maxLength;
        truncated = true;
      }
    } else if (node.nodeType === Node.ELEMENT_NODE) {
      // Process child nodes
      const children = Array.from(node.childNodes);
      let nodesToRemove = [];
      
      for (let i = 0; i < children.length; i++) {
        if (truncated) {
          // Collect remaining children to remove
          nodesToRemove = children.slice(i);
          break;
        }
        truncateNode(children[i]);
      }
      
      // Remove collected nodes
      nodesToRemove.forEach(child => {
        if (child.parentNode === node) {
          node.removeChild(child);
        }
      });
    }
  };
  
  truncateNode(tmp);
  
  return tmp.innerHTML;
};

const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  const date = new Date(dateString);
  return date.toLocaleString('fr-FR', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
};

export default function CorrectionHistory() {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [limit] = useState(12); // Cards per page
  const [total, setTotal] = useState(0);
  const [openDetailDialog, setOpenDetailDialog] = useState(false);
  const [selectedEntry, setSelectedEntry] = useState(null);

  // Filters
  const [filters, setFilters] = useState({
    userEmail: '',
    role: '',
    language: '',
    tab: '',
    dateFrom: '',
    dateTo: '',
  });

  useEffect(() => {
    fetchHistory();
  }, [page, filters]);

  const fetchHistory = async () => {
    try {
      setLoading(true);
      setError(null);

      const params = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
      });

      if (filters.userEmail) params.append('userEmail', filters.userEmail);
      if (filters.role) params.append('role', filters.role);
      if (filters.language) params.append('language', filters.language);
      if (filters.tab) params.append('tab', filters.tab);
      if (filters.dateFrom) params.append('dateFrom', filters.dateFrom);
      if (filters.dateTo) params.append('dateTo', filters.dateTo);

      const response = await apiClient.get(`/api/admin/correction-history?${params.toString()}`);
      setHistory(response.data || []);
      setTotal(response.total || 0);
    } catch (err) {
      console.error('Error fetching correction history:', err);
      setError('Erreur lors du chargement de l\'historique');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value,
    }));
    setPage(1); // Reset to first page when filtering
  };

  const handleOpenDetailDialog = (entry) => {
    setSelectedEntry(entry);
    setOpenDetailDialog(true);
  };

  const handleCloseDetailDialog = () => {
    setOpenDetailDialog(false);
    setSelectedEntry(null);
  };

  const handlePageChange = (event, value) => {
    setPage(value);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const totalPages = Math.ceil(total / limit);

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Historique des corrections
      </Typography>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              label="Email utilisateur"
              value={filters.userEmail}
              onChange={(e) => handleFilterChange('userEmail', e.target.value)}
              size="small"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Rôle</InputLabel>
              <Select
                value={filters.role}
                label="Rôle"
                onChange={(e) => handleFilterChange('role', e.target.value)}
              >
                <MenuItem value="">Tous</MenuItem>
                <MenuItem value="vet">Vétérinaire</MenuItem>
                <MenuItem value="generalist">Généraliste</MenuItem>
                <MenuItem value="dentist">Dentiste</MenuItem>
                <MenuItem value="orthodentist">Orthodontiste</MenuItem>
                <MenuItem value="assistant">Assistant</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Langue</InputLabel>
              <Select
                value={filters.language}
                label="Langue"
                onChange={(e) => handleFilterChange('language', e.target.value)}
              >
                <MenuItem value="">Toutes</MenuItem>
                <MenuItem value="fr">Français</MenuItem>
                <MenuItem value="en">Anglais</MenuItem>
                <MenuItem value="de">Allemand</MenuItem>
                <MenuItem value="it">Italien</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Onglet</InputLabel>
              <Select
                value={filters.tab}
                label="Onglet"
                onChange={(e) => handleFilterChange('tab', e.target.value)}
              >
                <MenuItem value="">Tous</MenuItem>
                <MenuItem value="dictaphone">Dictaphone</MenuItem>
                <MenuItem value="rapport">Rapport</MenuItem>
                <MenuItem value="retour_patient">Retour patient</MenuItem>
                <MenuItem value="assurance">Assurance</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              fullWidth
              label="Date de début"
              type="date"
              value={filters.dateFrom}
              onChange={(e) => handleFilterChange('dateFrom', e.target.value)}
              size="small"
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              fullWidth
              label="Date de fin"
              type="date"
              value={filters.dateTo}
              onChange={(e) => handleFilterChange('dateTo', e.target.value)}
              size="small"
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={1}>
            <Button
              fullWidth
              variant="outlined"
              onClick={() => {
                setFilters({
                  userEmail: '',
                  role: '',
                  language: '',
                  tab: '',
                  dateFrom: '',
                  dateTo: '',
                });
                setPage(1);
              }}
            >
              Réinitialiser
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Loading State */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          {/* Cards Grid */}
          {history.length === 0 ? (
            <Paper sx={{ p: 4, textAlign: 'center' }}>
              <Typography variant="h6" color="text.secondary">
                Aucun historique trouvé
              </Typography>
            </Paper>
          ) : (
            <Grid container spacing={3}>
              {history.map((entry) => (
                <Grid item xs={12} sm={6} md={4} key={entry.id}>
                  <Card
                    sx={{
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      cursor: 'pointer',
                      '&:hover': {
                        boxShadow: 6,
                      },
                    }}
                    onClick={() => handleOpenDetailDialog(entry)}
                  >
                    <CardContent sx={{ flexGrow: 1 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', mb: 2 }}>
                        <Typography variant="h6" component="div" noWrap>
                          {entry.user_email}
                        </Typography>
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleOpenDetailDialog(entry);
                          }}
                        >
                          <VisibilityIcon />
                        </IconButton>
                      </Box>

                      <Box sx={{ mb: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        {entry.role && (
                          <Chip label={entry.role} size="small" color="primary" variant="outlined" />
                        )}
                        {entry.language && (
                          <Chip label={entry.language.toUpperCase()} size="small" color="secondary" variant="outlined" />
                        )}
                        {entry.tab && (
                          <Chip 
                            label={entry.tab === 'dictaphone' ? 'Dictaphone' : 
                                   entry.tab === 'rapport' ? 'Rapport' :
                                   entry.tab === 'retour_patient' ? 'Retour patient' :
                                   entry.tab === 'assurance' ? 'Assurance' : entry.tab} 
                            size="small" 
                            color="success" 
                            variant="outlined" 
                          />
                        )}
                      </Box>

                      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                        {formatDate(entry.created_at)}
                      </Typography>

                      <Divider sx={{ my: 1 }} />

                      <Box sx={{ mb: 1 }}>
                        <Typography variant="body2" color="text.secondary" component="span" sx={{ fontWeight: 'bold' }}>
                          Texte original:
                        </Typography>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          component="div"
                          sx={{ mt: 0.5 }}
                          dangerouslySetInnerHTML={{ __html: truncateHtml(entry.original_text) }}
                        />
                      </Box>

                      <Box sx={{ mb: 1 }}>
                        <Typography variant="body2" color="text.secondary" component="span" sx={{ fontWeight: 'bold' }}>
                          Correction:
                        </Typography>
                        <Typography variant="body2" color="text.secondary" component="div" sx={{ mt: 0.5 }}>
                          {truncateHtml(entry.correction_text)}
                        </Typography>
                      </Box>

                      <Box>
                        <Typography variant="body2" color="text.secondary" component="span" sx={{ fontWeight: 'bold' }}>
                          Généré:
                        </Typography>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          component="div"
                          sx={{ mt: 0.5 }}
                          dangerouslySetInnerHTML={{ __html: truncateHtml(entry.newly_generated_text) }}
                        />
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
              <Pagination
                count={totalPages}
                page={page}
                onChange={handlePageChange}
                color="primary"
                size="large"
              />
            </Box>
          )}

          <Typography variant="body2" color="text.secondary" sx={{ mt: 2, textAlign: 'center' }}>
            Total: {total} entrée(s)
          </Typography>
        </>
      )}

      {/* Detail Dialog */}
      <Dialog
        open={openDetailDialog}
        onClose={handleCloseDetailDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">Détails de la correction</Typography>
            <IconButton onClick={handleCloseDetailDialog} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {selectedEntry && (
            <Box>
              {/* Metadata */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Informations
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2">
                      <strong>Email:</strong> {selectedEntry.user_email}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2">
                      <strong>Date:</strong> {formatDate(selectedEntry.created_at)}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2">
                      <strong>Rôle:</strong> {selectedEntry.role || 'N/A'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2">
                      <strong>Langue:</strong> {selectedEntry.language ? selectedEntry.language.toUpperCase() : 'N/A'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2">
                      <strong>Onglet:</strong> {selectedEntry.tab ? (
                        selectedEntry.tab === 'dictaphone' ? 'Dictaphone' :
                        selectedEntry.tab === 'rapport' ? 'Rapport' :
                        selectedEntry.tab === 'retour_patient' ? 'Retour patient' :
                        selectedEntry.tab === 'assurance' ? 'Assurance' : selectedEntry.tab
                      ) : 'N/A'}
                    </Typography>
                  </Grid>
                </Grid>
              </Box>

              <Divider sx={{ my: 2 }} />

              {/* Original Text */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Texte original
                </Typography>
                <Paper
                  sx={{
                    p: 2,
                    bgcolor: 'grey.50',
                    maxHeight: '200px',
                    overflow: 'auto',
                  }}
                >
                  {selectedEntry.original_text ? (
                    <Typography
                      variant="body2"
                      component="div"
                      dangerouslySetInnerHTML={{ __html: selectedEntry.original_text }}
                    />
                  ) : (
                    <Typography variant="body2">N/A</Typography>
                  )}
                </Paper>
              </Box>

              {/* Correction Text */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Instructions de correction
                </Typography>
                <Paper
                  sx={{
                    p: 2,
                    bgcolor: 'grey.50',
                    maxHeight: '200px',
                    overflow: 'auto',
                    whiteSpace: 'pre-wrap',
                  }}
                >
                  <Typography variant="body2">
                    {selectedEntry.correction_text || 'N/A'}
                  </Typography>
                </Paper>
              </Box>

              {/* Generated Text */}
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Texte généré
                </Typography>
                <Paper
                  sx={{
                    p: 2,
                    bgcolor: 'grey.50',
                    maxHeight: '200px',
                    overflow: 'auto',
                  }}
                >
                  {selectedEntry.newly_generated_text ? (
                    <Typography
                      variant="body2"
                      component="div"
                      dangerouslySetInnerHTML={{ __html: selectedEntry.newly_generated_text }}
                    />
                  ) : (
                    <Typography variant="body2">N/A</Typography>
                  )}
                </Paper>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDetailDialog}>Fermer</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

